import React from "react";
import "./App.css";
import Router from "./Router";
import { BrowserRouter } from "react-router-dom";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";
import { prefixer } from "stylis";
import rtlPlugin from "@mui/stylis-plugin-rtl";
import { ConfigProvider, theme as antTheme } from "antd";
import { createTheme, ThemeProvider as MuiThemeProvider } from "@mui/material";
import { ThemeProvider, useTheme } from "./app/context/ThemeContext";
import { PulseProvider } from "./app/components/PulseFab";
import ScrollToTop from "./app/components/RouteToTop";
import { useTranslation } from 'react-i18next';

const rtlCache = createCache({
  key: "muirtl",
  stylisPlugins: [prefixer, rtlPlugin],
});
const ltrCache = createCache({ key: "muiltr" });

const DynamicThemeProvider = ({ children }) => {
  const { currentTheme, theme } = useTheme();
  const { i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const isDark = currentTheme === 'dark';

  const muiTheme = createTheme({
    direction: isRTL ? 'rtl' : 'ltr',
    palette: {
      mode: currentTheme,
      primary: { main: theme.primaryColor, secondary: theme.secColor3 },
      secondary: { main: theme.secColor3 },
      background: { default: theme.appBg, paper: theme.cardSolid || theme.cardBg },
      text: { primary: theme.textPrimary, secondary: theme.textSecondary },
      divider: theme.borderColor
    },
    typography: {
      fontFamily: isRTL ? "Cairo, Plus Jakarta Sans, sans-serif" : "Plus Jakarta Sans, sans-serif"
    },
    components: {
      MuiAppBar: { styleOverrides: { root: { backgroundColor: theme.appBg, color: theme.textPrimary } } },
      MuiCard: { styleOverrides: { root: { backgroundColor: theme.cardSolid || theme.cardBg, backgroundImage: 'none', color: theme.textPrimary } } },
      MuiDialog: {
        styleOverrides: {
          paper: {
            backgroundColor: theme.modalBg,
            backgroundImage: 'none',
            color: theme.textPrimary,
            opacity: 1,
          },
        },
      },
      MuiBackdrop: {
        styleOverrides: {
          root: { backgroundColor: theme.modalMask, backdropFilter: 'none' },
        },
      },
    }
  });

  const antdTheme = {
    algorithm: isDark ? antTheme.darkAlgorithm : antTheme.defaultAlgorithm,
    token: {
      colorPrimary: theme.primaryColor,
      colorBgContainer: isDark ? 'transparent' : '#ffffff',
      colorBgElevated: theme.modalBg,
      colorBgMask: theme.modalMask,
      colorText: theme.textPrimary,
      colorTextSecondary: theme.textSecondary,
      colorBorder: theme.borderColor,
      borderRadius: 12,
      fontFamily: isRTL ? "Cairo, Plus Jakarta Sans, sans-serif" : "Plus Jakarta Sans, sans-serif",
    },
    components: {
      Table: {
        headerBg: theme.tableHeaderBg,
        rowHoverBg: theme.hoverBg,
        borderColor: theme.tableBorder,
        colorBgContainer: 'transparent',
      },
      Notification: {
        colorText: theme.textPrimary,
        colorTextHeading: theme.textPrimary,
        colorIcon: theme.textSecondary,
        colorBgElevated: theme.modalBg,
      },
      Message: {
        contentBg: theme.modalBg,
        colorText: theme.textPrimary,
      },
    },
  };

  return (
    <CacheProvider value={isRTL ? rtlCache : ltrCache}>
      <MuiThemeProvider theme={muiTheme}>
        <ConfigProvider direction={isRTL ? 'rtl' : 'ltr'} theme={antdTheme}>
          {children}
        </ConfigProvider>
      </MuiThemeProvider>
    </CacheProvider>
  );
};

function App() {
  const { i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';

  React.useEffect(() => {
    document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
    document.documentElement.lang = i18n.language;
  }, [i18n.language, isRTL]);

  return (
    <ThemeProvider>
      <DynamicThemeProvider>
        <div className="App" dir={isRTL ? 'rtl' : 'ltr'}>
          <BrowserRouter>
            <PulseProvider>
              <ScrollToTop />
              <Router />
            </PulseProvider>
          </BrowserRouter>
        </div>
      </DynamicThemeProvider>
    </ThemeProvider>
  );
}

export default App;
